import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Card, DatePicker, Button, Select, Table, Modal, Tag, Drawer, Spin, Tabs } from 'antd';
import { IconChartBar, IconSnowflake, IconTruck, IconClock, IconFilter, 
  IconCalendarStats, IconUsers, IconBuildingCommunity } from '@tabler/icons-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, ScatterChart, Scatter
} from 'recharts';
import dayjs from 'dayjs';
import api from '../../../authentication/axiosInstance';
import styles from './JobsSummary.module.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

const COLORS = ['#034832', '#cdaf5e', '#FF8042', '#8884d8', '#82ca9d'];
const JOB_TYPE_COLORS = {
  Walkways: '#034832',
  Salting: '#cdaf5e',
  Plowing: '#FF8042',
  "Site Patrol": '#8884d8',
  Exception: '#82ca9d'
};


const JobsSummary = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [dateRange, setDateRange] = useState([dayjs().subtract(90, 'days'), dayjs()]);
  const [filters, setFilters] = useState({
    site: 'all',
    storm: 'all',
    type: 'all',
    vehicle: 'all',
    staff: 'all',
    client: 'all'
  });
  const [selectedSite, setSelectedSite] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleModalVisible, setVehicleModalVisible] = useState(false);
  const [summaryStats, setSummaryStats] = useState({
    totalJobs: 0,
    totalHours: {
      Walkway: 0,
      Salting: 0,
      Plowing: 0,
      Patrol: 0,
      Exception: 0
    },
    siteStats: [],
    stormStats: [],
    timelineStats: [],
    hourlyDistribution: [],
    vehicleStats: [],
    staffStats: [],
    weekdayDistribution: [],
    efficiencyMetrics: {
      avgJobDuration: 0,
      jobsPerDay: 0,
      utilizationRate: 0
    }
  });

  const formatNumber = (value, suffix = '') => {
    if (value === null || value === undefined) return 'N/A';
    const formatted = Number(value).toFixed(2);
    return formatted.endsWith('.00') 
      ? Math.round(value) + suffix
      : formatted + suffix;
  };

  useEffect(() => {
    fetchJobs();
  }, [dateRange]);

  useEffect(() => {
    calculateStats(jobs);
  }, [jobs, filters]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/debrief/jobs/');
      const filteredJobs = response.data.filter(job => {
        const jobDate = dayjs(job.job_start_date);
        return jobDate.isAfter(dateRange[0]) && jobDate.isBefore(dateRange[1]);
      });
      setJobs(filteredJobs);
    } catch (error) {
      console.error('Failed to fetch jobs data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (jobsData) => {
    // Filter jobs based on current filters
    const filteredJobs = jobsData.filter(job => {
      const siteMatch = filters.site === 'all' || job.site_details?.site_id === filters.site;
      const stormMatch = filters.storm === 'all' || job.storm_details?.storm_id === filters.storm;
      const typeMatch = filters.type === 'all' || job.type === filters.type;
      const vehicleMatch = filters.vehicle === 'all' || job.vehicle_details?.vehicle_id === filters.vehicle;
      const staffMatch = filters.staff === 'all' || job.staff_details?.some(s => s.staff_id === filters.staff);
      const clientMatch = filters.client === 'all' || job.site_details?.client === filters.client;
      return siteMatch && stormMatch && typeMatch && vehicleMatch && staffMatch && clientMatch;
    });

    // Calculate total hours by type
    const totalHours = {
      Walkway: 0,
      Salting: 0,
      Plowing: 0,
      Patrol: 0,
      Exception: 0
    };

    filteredJobs.forEach(job => {
      if (job.job_start_date && job.job_end_date && job.type) {
        const duration = dayjs(job.job_end_date).diff(dayjs(job.job_start_date), 'hour', true);
        totalHours[job.type] = (totalHours[job.type] || 0) + duration;
      }
    });

    // Calculate site statistics
    const siteStats = Object.values(filteredJobs.reduce((acc, job) => {
      const siteId = job.site_details?.site_id;
      const siteName = job.site_details?.site_name;
      if (siteId && siteName) {
        if (!acc[siteId]) {
          acc[siteId] = {
            siteId,
            siteName,
            totalJobs: 0,
            totalHours: 0,
            jobsByType: {},
            region: job.site_details?.region,
            totalCrewSize: 0,
            avgCrewSize: 0,
            totalManHours: 0,
            jobs: [] // Store actual jobs for detailed view
          };
        }
        acc[siteId].totalJobs++;
        acc[siteId].jobs.push(job);
        if (job.job_start_date && job.job_end_date) {
          const duration = dayjs(job.job_end_date).diff(dayjs(job.job_start_date), 'hour', true);
          acc[siteId].totalHours += duration;
          acc[siteId].jobsByType[job.type] = (acc[siteId].jobsByType[job.type] || 0) + 1;
          
          // Add crew size and man hours calculations
          if (job.crew_size) {
            acc[siteId].totalCrewSize += job.crew_size;
            acc[siteId].avgCrewSize = acc[siteId].totalCrewSize / acc[siteId].totalJobs;
          }
          if (job.man_hours) {
            acc[siteId].totalManHours += job.man_hours;
          }
        }
      }
      return acc;
    }, {}));

    // Calculate vehicle statistics
    const vehicleStats = Object.values(filteredJobs.reduce((acc, job) => {
      const vehicleId = job.vehicle_details?.vehicle_id;
      if (vehicleId) {
        if (!acc[vehicleId]) {
          acc[vehicleId] = {
            vehicleId,
            vehicleName: job.vehicle_details?.vehicle_name,
            vehicleType: job.vehicle_details?.vehicle_type,
            totalJobs: 0,
            totalHours: 0,
            jobsByType: {},
            jobs: [] // Store actual jobs for detailed view
          };
        }
        acc[vehicleId].totalJobs++;
        acc[vehicleId].jobs.push(job);
        if (job.job_start_date && job.job_end_date) {
          const duration = dayjs(job.job_end_date).diff(dayjs(job.job_start_date), 'hour', true);
          acc[vehicleId].totalHours += duration;
          acc[vehicleId].jobsByType[job.type] = (acc[vehicleId].jobsByType[job.type] || 0) + 1;
        }
      }
      return acc;
    }, {}));

    // Calculate staff statistics
    const staffStats = filteredJobs.reduce((acc, job) => {
      job.staff_details?.forEach(staff => {
        const staffId = staff.staff_id;
        if (!acc[staffId]) {
          acc[staffId] = {
            staffId,
            staffName: staff.staff_name,
            totalJobs: 0,
            totalHours: 0,
            jobsByType: {}
          };
        }
        acc[staffId].totalJobs++;
        if (job.job_start_date && job.job_end_date) {
          const duration = dayjs(job.job_end_date).diff(dayjs(job.job_start_date), 'hour', true);
          acc[staffId].totalHours += duration;
          acc[staffId].jobsByType[job.type] = (acc[staffId].jobsByType[job.type] || 0) + 1;
        }
      });
      return acc;
    }, {});

    // Calculate weekday distribution
    const weekdayDistribution = Array(7).fill(0).map((_, index) => ({
      day: dayjs().day(index).format('dddd'),
      count: filteredJobs.filter(job => dayjs(job.job_start_date).day() === index).length,
      hours: filteredJobs
        .filter(job => dayjs(job.job_start_date).day() === index)
        .reduce((acc, job) => {
          if (job.job_start_date && job.job_end_date) {
            return acc + dayjs(job.job_end_date).diff(dayjs(job.job_start_date), 'hour', true);
          }
          return acc;
        }, 0)
    }));

    // Calculate efficiency metrics
    const totalDuration = filteredJobs.reduce((acc, job) => {
      if (job.job_start_date && job.job_end_date) {
        return acc + dayjs(job.job_end_date).diff(dayjs(job.job_start_date), 'hour', true);
      }
      return acc;
    }, 0);

    const uniqueDays = new Set(filteredJobs.map(job => dayjs(job.job_start_date).format('YYYY-MM-DD'))).size;

    const efficiencyMetrics = {
      avgJobDuration: filteredJobs.length ? totalDuration / filteredJobs.length : 0,
      jobsPerDay: uniqueDays ? filteredJobs.length / uniqueDays : 0,
      utilizationRate: uniqueDays ? (totalDuration / (uniqueDays * 24)) * 100 : 0
    };

    // Calculate hourly distribution (24-hour view)
    const hourlyDistribution = Array(24).fill(0).map((_, hour) => ({
      hour: hour.toString().padStart(2, '0') + ':00',
      jobs: filteredJobs.filter(job => dayjs(job.job_start_date).hour() === hour).length,
      hours: filteredJobs
        .filter(job => dayjs(job.job_start_date).hour() === hour)
        .reduce((acc, job) => {
          if (job.job_start_date && job.job_end_date) {
            return acc + dayjs(job.job_end_date).diff(dayjs(job.job_start_date), 'hour', true);
          }
          return acc;
        }, 0)
    }));

    // Calculate monthly trend
    const monthlyTrend = Object.values(filteredJobs.reduce((acc, job) => {
      const month = dayjs(job.job_start_date).format('YYYY-MM');
      if (!acc[month]) {
        acc[month] = {
          month,
          jobs: 0,
          hours: 0,
          byType: {}
        };
      }
      acc[month].jobs++;
      if (job.job_start_date && job.job_end_date) {
        const duration = dayjs(job.job_end_date).diff(dayjs(job.job_start_date), 'hour', true);
        acc[month].hours += duration;
        acc[month].byType[job.type] = (acc[month].byType[job.type] || 0) + 1;
      }
      return acc;
    }, {})).sort((a, b) => dayjs(a.month).diff(dayjs(b.month)));

    // Calculate average duration by job type
    const avgDurationByType = Object.entries(
      filteredJobs.reduce((acc, job) => {
        if (job.type && job.job_start_date && job.job_end_date) {
          if (!acc[job.type]) {
            acc[job.type] = { total: 0, count: 0 };
          }
          acc[job.type].total += dayjs(job.job_end_date).diff(dayjs(job.job_start_date), 'hour', true);
          acc[job.type].count++;
        }
        return acc;
      }, {})
    ).map(([type, data]) => ({
      type,
      avgDuration: data.total / data.count
    }));

    setSummaryStats({
      totalJobs: filteredJobs.length,
      totalHours,
      siteStats,
      vehicleStats,
      staffStats,
      weekdayDistribution,
      efficiencyMetrics,
      hourlyDistribution,
      monthlyTrend,
      avgDurationByType
    });
  };

  const handleSiteClick = (record) => {
    setSelectedSite(record);
    setDrawerVisible(true);
  };

  const handleVehicleClick = (record) => {
    setSelectedVehicle(record);
    setVehicleModalVisible(true);
  };

  const columns = [
    {
      title: 'Site',
      dataIndex: 'siteName',
      key: 'siteName',
      sorter: (a, b) => a.siteName.localeCompare(b.siteName),
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => handleSiteClick(record)}
          style={{ color: '#034832', padding: 0 }}
        >
          {text}
        </Button>
      )
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      sorter: (a, b) => a.region?.localeCompare(b.region)
    },
    {
      title: 'Total Jobs',
      dataIndex: 'totalJobs',
      key: 'totalJobs',
      sorter: (a, b) => a.totalJobs - b.totalJobs
    },
    {
      title: 'Total Hours',
      dataIndex: 'totalHours',
      key: 'totalHours',
      render: (hours) => formatNumber(hours, 'h'),
      sorter: (a, b) => a.totalHours - b.totalHours
    },
    {
      title: 'Avg Crew Size',
      dataIndex: 'avgCrewSize',
      key: 'avgCrewSize',
      render: (size) => size ? formatNumber(size) : 'N/A',
      sorter: (a, b) => (a.avgCrewSize || 0) - (b.avgCrewSize || 0)
    },
    {
      title: 'Total Man Hours',
      dataIndex: 'totalManHours',
      key: 'totalManHours',
      render: (hours) => hours ? formatNumber(hours, 'h') : 'N/A',
      sorter: (a, b) => (a.totalManHours || 0) - (b.totalManHours || 0)
    },
    {
      title: 'Job Types',
      key: 'jobTypes',
      render: (_, record) => (
        <span>
          {Object.entries(record.jobsByType).map(([type, count]) => (
            <Tag color={JOB_TYPE_COLORS[type]} key={type}>
              {type}: {count}
            </Tag>
          ))}
        </span>
      )
    }
  ];

  const vehicleColumns = [
    {
      title: 'Vehicle',
      dataIndex: 'vehicleName',
      key: 'vehicleName',
      sorter: (a, b) => a.vehicleName.localeCompare(b.vehicleName),
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => handleVehicleClick(record)}
          style={{ color: '#034832', padding: 0 }}
        >
          {text}
        </Button>
      )
    },
    {
      title: 'Type',
      dataIndex: 'vehicleType',
      key: 'vehicleType',
      sorter: (a, b) => a.vehicleType?.localeCompare(b.vehicleType)
    },
    {
      title: 'Total Jobs',
      dataIndex: 'totalJobs',
      key: 'totalJobs',
      sorter: (a, b) => a.totalJobs - b.totalJobs
    },
    {
      title: 'Total Hours',
      dataIndex: 'totalHours',
      key: 'totalHours',
      render: (hours) => formatNumber(hours, 'h'),
      sorter: (a, b) => a.totalHours - b.totalHours
    },
    {
      title: 'Job Types',
      key: 'jobTypes',
      render: (_, record) => (
        <span>
          {Object.entries(record.jobsByType).map(([type, count]) => (
            <Tag color={JOB_TYPE_COLORS[type]} key={type}>
              {type}: {count}
            </Tag>
          ))}
        </span>
      )
    }
  ];

  const jobDetailColumns = [
    {
      title: 'Date',
      dataIndex: 'job_start_date',
      key: 'date',
      render: (date) => dayjs(date).format('MMM D, YYYY h:mm A'),
      sorter: (a, b) => dayjs(a.job_start_date).diff(dayjs(b.job_start_date))
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag color={JOB_TYPE_COLORS[type]}>{type}</Tag>
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => {
        const duration = dayjs(record.job_end_date).diff(dayjs(record.job_start_date), 'hour', true);
        return formatNumber(duration, 'h');
      },
      sorter: (a, b) => {
        const durationA = dayjs(a.job_end_date).diff(dayjs(a.job_start_date));
        const durationB = dayjs(b.job_end_date).diff(dayjs(b.job_start_date));
        return durationA - durationB;
      }
    },
    {
      title: 'Vehicle',
      dataIndex: ['vehicle_details', 'vehicle_name'],
      key: 'vehicle'
    },
    {
      title: 'Staff',
      dataIndex: 'staff_details',
      key: 'staff',
      render: (staff) => staff?.map(s => s.staff_name).join(', ')
    }
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <Typography variant="h4" className={styles.title}>Jobs Summary</Typography>
          <Typography variant="body1" className={styles.subtitle}>
            Comprehensive analysis of job statistics and performance metrics
          </Typography>
        </div>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            className={styles.datePicker}
          />
          <Button
            type="primary"
            icon={<IconFilter size={16} />}
            onClick={fetchJobs}
            className={styles.refreshButton}
            style={{ backgroundColor: '#034832', borderColor: '#034832' }}
          >
            Apply Filters
          </Button>
        </Box>
      </div>

      <div className={styles.filters}>
        <Select
          value={filters.client}
          onChange={(value) => setFilters(prev => ({ ...prev, client: value }))}
          style={{ width: 200 }}
          placeholder="Filter by Client"
        >
          <Option value="all">All Clients</Option>
          {Array.from(new Set(jobs.map(job => job.site_details?.client))).map(client => (
            client && <Option key={client} value={client}>{client}</Option>
          ))}
        </Select>

        <Select
          value={filters.site}
          onChange={(value) => setFilters(prev => ({ ...prev, site: value }))}
          style={{ width: 200 }}
          placeholder="Filter by Site"
        >
          <Option value="all">All Sites</Option>
          {Array.from(new Set(jobs.map(job => job.site_details?.site_id)))
            .map(siteId => {
              const site = jobs.find(job => job.site_details?.site_id === siteId)?.site_details;
              if (site && (filters.client === 'all' || site.client === filters.client)) {
                return (
                  <Option key={siteId} value={siteId}>{site.site_name}</Option>
                );
              }
              return null;
            })
            .filter(Boolean)}
        </Select>

        <Select
          value={filters.storm}
          onChange={(value) => setFilters(prev => ({ ...prev, storm: value }))}
          style={{ width: 300 }}
          placeholder="Filter by Storm"
        >
          <Option value="all">All Storms</Option>
          {Array.from(new Set(jobs.map(job => job.storm_details?.storm_id))).map(stormId => {
            const storm = jobs.find(job => job.storm_details?.storm_id === stormId)?.storm_details;
            return storm && (
              <Option key={stormId} value={stormId}>{storm.storm_type} - {dayjs(storm.date_of_storm).format('MMM D, YYYY')}</Option>
            );
          })}
        </Select>

        <Select
          value={filters.type}
          onChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
          style={{ width: 200 }}
          placeholder="Filter by Type"
        >
          <Option value="all">All Types</Option>
          {Array.from(new Set(jobs.map(job => job.type))).map(type => (
            type && <Option key={type} value={type}>{type}</Option>
          ))}
        </Select>

        <Select
          value={filters.vehicle}
          onChange={(value) => setFilters(prev => ({ ...prev, vehicle: value }))}
          style={{ width: 200 }}
          placeholder="Filter by Vehicle"
        >
          <Option value="all">All Vehicles</Option>
          {Array.from(new Set(jobs.map(job => job.vehicle_details?.vehicle_id))).map(vehicleId => {
            const vehicle = jobs.find(job => job.vehicle_details?.vehicle_id === vehicleId)?.vehicle_details;
            return vehicle && (
              <Option key={vehicleId} value={vehicleId}>{vehicle.vehicle_name}</Option>
            );
          })}
        </Select>

        <Select
          value={filters.staff}
          onChange={(value) => setFilters(prev => ({ ...prev, staff: value }))}
          style={{ width: 200 }}
          placeholder="Filter by Staff"
        >
          <Option value="all">All Staff</Option>
          {Array.from(new Set(jobs.flatMap(job => 
            job.staff_details?.map(staff => staff.staff_id) || []
          ))).map(staffId => {
            const staff = jobs.find(job => 
              job.staff_details?.some(s => s.staff_id === staffId)
            )?.staff_details?.find(s => s.staff_id === staffId);
            return staff && (
              <Option key={staffId} value={staffId}>{staff.staff_name}</Option>
            );
          })}
        </Select>
      </div>

      <div className={styles.statsCards}>
        <Card className={styles.statCard}>
          <IconChartBar size={24} className={styles.statIcon} />
          <Typography variant="body2" className={styles.statLabel}>Total Jobs</Typography>
          <Typography variant="h5" className={styles.statValue}>
            {summaryStats.totalJobs}
          </Typography>
        </Card>

        <Card className={styles.statCard}>
          <IconClock size={24} className={styles.statIcon} />
          <Typography variant="body2" className={styles.statLabel}>Total Hours</Typography>
          <Typography variant="h5" className={styles.statValue}>
            {formatNumber(Object.values(summaryStats.totalHours).reduce((a, b) => a + b, 0), 'h')}
          </Typography>
        </Card>

        <Card className={styles.statCard}>
          <IconCalendarStats size={24} className={styles.statIcon} />
          <Typography variant="body2" className={styles.statLabel}>Avg. Jobs/Day</Typography>
          <Typography variant="h5" className={styles.statValue}>
            {formatNumber(summaryStats.efficiencyMetrics.jobsPerDay)}
          </Typography>
        </Card>

        <Card className={styles.statCard}>
          <IconUsers size={24} className={styles.statIcon} />
          <Typography variant="body2" className={styles.statLabel}>Active Staff</Typography>
          <Typography variant="h5" className={styles.statValue}>
            {Object.keys(summaryStats.staffStats).length}
          </Typography>
        </Card>

        <Card className={styles.statCard}>
          <IconUsers size={24} className={styles.statIcon} />
          <Typography variant="body2" className={styles.statLabel}>Average Crew Size</Typography>
          <Typography variant="h5" className={styles.statValue}>
            {formatNumber(summaryStats.siteStats.reduce((acc, site) => acc + (site.avgCrewSize || 0), 0) / 
             summaryStats.siteStats.filter(site => site.avgCrewSize).length || 0)}
          </Typography>
        </Card>

        <Card className={styles.statCard}>
          <IconClock size={24} className={styles.statIcon} />
          <Typography variant="body2" className={styles.statLabel}>Total Man Hours</Typography>
          <Typography variant="h5" className={styles.statValue}>
            {formatNumber(summaryStats.siteStats.reduce((acc, site) => acc + (site.totalManHours || 0), 0), 'h')}
          </Typography>
        </Card>
      </div>

      <Tabs 
        defaultActiveKey="1" 
        className={styles.tabs}
        tabBarStyle={{
          color: '#034832',
          fontWeight: 500
        }}
      >
        <TabPane tab={<span style={{ color: '#034832' }}>Site Performance</span>} key="1">
          <Card className={styles.tableCard}>
            <Typography variant="h6" className={styles.tableTitle}>Site Performance Summary</Typography>
            <Table
              columns={columns}
              dataSource={summaryStats.siteStats}
              rowKey="siteId"
              scroll={{ x: true }}
              loading={loading}
            />
          </Card>
        </TabPane>

        <TabPane tab={<span style={{ color: '#034832' }}>Vehicle Performance</span>} key="2">
          <Card className={styles.tableCard}>
            <Typography variant="h6" className={styles.tableTitle}>Vehicle Performance Summary</Typography>
            <Table
              columns={vehicleColumns}
              dataSource={summaryStats.vehicleStats}
              rowKey="vehicleId"
              scroll={{ x: true }}
              loading={loading}
            />
          </Card>
        </TabPane>

        <TabPane tab={<span style={{ color: '#034832' }}>Time Analysis</span>} key="3">
          <div className={styles.chartsContainer}>
            <Card className={styles.chartCard}>
              <Typography variant="h6" className={styles.chartTitle}>Hours by Job Type</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(summaryStats.totalHours || {})
                      .filter(([_, value]) => value > 0)
                      .map(([name, value]) => ({
                        name,
                        value: value || 0
                      }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, value }) => `${name}: ${value.toFixed(2)}h`}
                  >
                    {Object.entries(summaryStats.totalHours || {})
                      .filter(([_, value]) => value > 0)
                      .map((entry, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatNumber(value, 'h')} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            <Card className={styles.chartCard}>
              <Typography variant="h6" className={styles.chartTitle}>Weekly Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={summaryStats.weekdayDistribution || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis yAxisId="left" orientation="left" stroke="#034832" />
                  <YAxis yAxisId="right" orientation="right" stroke="#cdaf5e" />
                  <Tooltip formatter={(value, name) => [formatNumber(value, name === 'Hours' ? 'h' : ''), name]} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="count" name="Jobs" fill="#034832" />
                  <Bar yAxisId="right" dataKey="hours" name="Hours" fill="#cdaf5e" />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className={styles.chartCard}>
              <Typography variant="h6" className={styles.chartTitle}>24-Hour Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={summaryStats.hourlyDistribution || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis yAxisId="left" orientation="left" stroke="#034832" />
                  <YAxis yAxisId="right" orientation="right" stroke="#cdaf5e" />
                  <Tooltip formatter={(value, name) => [formatNumber(value, name === 'Hours' ? 'h' : ''), name]} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="jobs" name="Jobs" stroke="#034832" />
                  <Line yAxisId="right" type="monotone" dataKey="hours" name="Hours" stroke="#cdaf5e" />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card className={styles.chartCard}>
              <Typography variant="h6" className={styles.chartTitle}>Monthly Trend</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={summaryStats.monthlyTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tickFormatter={(value) => dayjs(value).format('MMM YY')} />
                  <YAxis yAxisId="left" orientation="left" stroke="#034832" />
                  <YAxis yAxisId="right" orientation="right" stroke="#cdaf5e" />
                  <Tooltip 
                    labelFormatter={(value) => dayjs(value).format('MMMM YYYY')}
                    formatter={(value, name) => [formatNumber(value, name === 'Hours' ? 'h' : ''), name]}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="jobs" name="Jobs" fill="#034832" />
                  <Bar yAxisId="right" dataKey="hours" name="Hours" fill="#cdaf5e" />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className={styles.chartCard}>
              <Typography variant="h6" className={styles.chartTitle}>Average Duration by Job Type</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={summaryStats.avgDurationByType || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatNumber(value, 'h')} />
                  <Legend />
                  <Bar dataKey="avgDuration" name="Average Hours" fill="#034832">
                    {(summaryStats.avgDurationByType || []).map((entry, index) => (
                      <Cell key={index} fill={JOB_TYPE_COLORS[entry.type] || COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className={styles.chartCard}>
              <Typography variant="h6" className={styles.chartTitle}>Job Types Over Time</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={summaryStats.monthlyTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tickFormatter={(value) => dayjs(value).format('MMM YY')} />
                  <YAxis />
                  <Tooltip labelFormatter={(value) => dayjs(value).format('MMMM YYYY')} />
                  <Legend />
                  {Object.keys(JOB_TYPE_COLORS).map((type) => (
                    <Area
                      key={type}
                      type="monotone"
                      dataKey={`byType.${type}`}
                      name={type}
                      stackId="1"
                      fill={JOB_TYPE_COLORS[type]}
                      stroke={JOB_TYPE_COLORS[type]}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card className={styles.chartCard}>
              <Typography variant="h6" className={styles.chartTitle}>Crew Size and Man Hours</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={summaryStats.siteStats || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="siteName" angle={-45} textAnchor="end" interval={0} height={100} />
                  <YAxis yAxisId="left" orientation="left" stroke="#034832" />
                  <YAxis yAxisId="right" orientation="right" stroke="#cdaf5e" />
                  <Tooltip formatter={(value, name) => [
                    formatNumber(value, name === 'Total Man Hours' ? 'h' : ''),
                    name
                  ]} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="avgCrewSize" name="Avg Crew Size" fill="#034832" />
                  <Bar yAxisId="right" dataKey="totalManHours" name="Total Man Hours" fill="#cdaf5e" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </TabPane>
      </Tabs>

      <Drawer
        title={`Jobs for ${selectedSite?.siteName}`}
        placement="right"
        width={800}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
      >
        {selectedSite && (
          <>
            <div className={styles.drawerStats}>
              <Card className={styles.miniStatCard}>
                <Typography variant="body2">Total Jobs</Typography>
                <Typography variant="h6">{selectedSite.totalJobs}</Typography>
              </Card>
              <Card className={styles.miniStatCard}>
                <Typography variant="body2">Total Hours</Typography>
                <Typography variant="h6">{formatNumber(selectedSite.totalHours, 'h')}</Typography>
              </Card>
              <Card className={styles.miniStatCard}>
                <Typography variant="body2">Region</Typography>
                <Typography variant="h6">{selectedSite.region}</Typography>
              </Card>
            </div>
            <Table
              columns={jobDetailColumns}
              dataSource={selectedSite.jobs}
              rowKey="job_id"
              scroll={{ x: true }}
            />
          </>
        )}
      </Drawer>

      <Modal
        title={`Vehicle Performance: ${selectedVehicle?.vehicleName}`}
        visible={vehicleModalVisible}
        onCancel={() => setVehicleModalVisible(false)}
        width={800}
        footer={null}
      >
        {selectedVehicle && (
          <>
            <div className={styles.modalStats}>
              <Card className={styles.miniStatCard}>
                <Typography variant="body2">Total Jobs</Typography>
                <Typography variant="h6">{selectedVehicle.totalJobs}</Typography>
              </Card>
              <Card className={styles.miniStatCard}>
                <Typography variant="body2">Total Hours</Typography>
                <Typography variant="h6">{formatNumber(selectedVehicle.totalHours, 'h')}</Typography>
              </Card>
              <Card className={styles.miniStatCard}>
                <Typography variant="body2">Vehicle Type</Typography>
                <Typography variant="h6">{selectedVehicle.vehicleType}</Typography>
              </Card>
            </div>
            <Table
              columns={jobDetailColumns}
              dataSource={selectedVehicle.jobs}
              rowKey="job_id"
              scroll={{ x: true }}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default JobsSummary; 